import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { Input } from '@/components/ui/input'
import * as Mutation from '../../../../api/gql/mutation'
import { describeScheduleError } from '../../lib/errors'
import type { ScheduleActivity } from '../../types'
import IsoDatePicker from '../../components/IsoDatePicker'
import { useGanttOps } from '../gantt-ops'
import FieldRow from './FieldRow'

interface Props {
  activity: ScheduleActivity
  scheduleId: string
  canEdit: boolean
}

/**
 * Progress fields of the activity panel (Task 17, spec B §9.7 step 3). Never mounted for a summary
 * (`ActivityPanel` hides the whole section) — its progress is a rollup shown read-only in
 * `DatesSection` instead.
 *
 * `ActivityPanel` mounts this with `key={activity.id}`, so `pendingField`/`fieldError` reset for
 * free on focus change (fix round 1, review finding #3) along with `percent`'s own local state.
 */
const ProgressSection: React.FC<Props> = ({ activity, scheduleId, canEdit }) => {
  const { t } = useTranslation()
  const { run } = useGanttOps()
  const [setProgress] = useMutation(Mutation.SET_SCHEDULE_ACTIVITY_PROGRESS)
  const [pendingField, setPendingField] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)

  const [percent, setPercent] = useState(String(activity.percentComplete))
  useEffect(() => setPercent(String(activity.percentComplete)), [activity.id, activity.percentComplete])

  const errorFor = (field: string): string | null => (fieldError && fieldError.field === field ? fieldError.message : null)
  const clearError = (field: string) => setFieldError((e) => (e && e.field === field ? null : e))

  const patch = async (field: string, fields: Record<string, any>) => {
    setFieldError(null)
    setPendingField(field)
    try {
      await run(
        () => setProgress({ variables: { scheduleId, patches: [{ id: activity.id, ...fields }] } }) as unknown as Promise<{ data: any }>,
        (d: any) => d.setScheduleActivityProgress,
      )
    } catch (e: any) {
      setFieldError({ field, message: describeScheduleError(e.message, t) })
    } finally {
      setPendingField(null)
    }
  }

  const commitPercent = () => {
    const n = Number(percent)
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      setFieldError({ field: 'percent', message: t('Schedule_cell_invalid_percent') })
      setPercent(String(activity.percentComplete))
      return
    }
    clearError('percent')
    if (n === activity.percentComplete) return
    patch('percent', { percentComplete: n })
  }
  const onActualStartChange = (v: string | null) => patch('actualStart', { actualStart: v })
  const onActualFinishChange = (v: string | null) => patch('actualFinish', { actualFinish: v })

  const disabled = !canEdit

  return (
    <div>
      <FieldRow label={t('Schedule_field_percent')} pending={pendingField === 'percent'} error={errorFor('percent')}>
        {(id) => (
          <Input id={id} type="number" min={0} max={100} className="h-8 text-sm" value={percent} disabled={disabled}
            onChange={(e) => setPercent(e.target.value)} onBlur={commitPercent} />
        )}
      </FieldRow>
      <FieldRow label={t('Schedule_field_actual_start')} pending={pendingField === 'actualStart'} error={errorFor('actualStart')}>
        {(id) => <IsoDatePicker id={id} size="sm" clearable value={activity.actualStart} disabled={disabled} onChange={onActualStartChange} />}
      </FieldRow>
      <FieldRow label={t('Schedule_field_actual_finish')} pending={pendingField === 'actualFinish'} error={errorFor('actualFinish')}>
        {(id) => <IsoDatePicker id={id} size="sm" clearable value={activity.actualFinish} disabled={disabled} onChange={onActualFinishChange} />}
      </FieldRow>
    </div>
  )
}

export default ProgressSection
